import {
  Children,
  createContext,
  type ReactNode,
  type Ref,
  type RefCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import cx from 'classnames'
import { noop } from 'lodash-es'
import styles from './ModalSheet.module.css'
import { useOnClickOutside } from '@/hooks/click-outside-hooks'
import { IconButton } from '@/components/IconButton'

import { useModalStacking } from './ModalStackContext'
import { m, type MotionValue, motionValue, useTransform } from 'motion/react'
import { ScrollShadow } from '@/components/ScrollShadow'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { clamp } from '@/utils/math-util'
import { useCssTransition } from '@/hooks/transition-hooks'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { FullscreenOverlay } from '@/components/FullscreenOverlay'
import { type DropzoneProps } from '@/hooks/dropzone-hook'
import type { FittedIllustration } from '@/components/SpotIllustration/fitted-illustration'

/**
 * Modal sheet context props.
 */
interface ModalSheetContextProps {
  connectScrollRef: RefCallback<Element>
  scrollYStart: MotionValue<number>
  close?: () => unknown
  panel?: boolean
}

/**
 * Used to pass a ref from the `ModalSheetContent` around.
 */
export const ModalSheetContext = createContext<ModalSheetContextProps>({
  connectScrollRef: noop,
  scrollYStart: motionValue(0),
})

interface ModalSheetProps {
  children?: ReactNode
  showing: boolean
  disableCloseOnEsc?: boolean
  disableCloseOnOutsideClick?: boolean
  onRequestClose?: () => unknown
  large?: boolean
  /**
   * Slides in from the right as a full-height panel instead of a centered
   * sheet.
   */
  panel?: boolean
  leftSidePanelContent?: ReactNode
  dropzoneProps?: DropzoneProps<HTMLDivElement>
  ref?: Ref<HTMLDivElement>
  testId?: string
}

/**
 * A modal sheet.
 */
export function ModalSheet({
  showing,
  onRequestClose,
  disableCloseOnEsc,
  disableCloseOnOutsideClick,
  children,
  large,
  panel,
  leftSidePanelContent,
  dropzoneProps,
  testId,
  ref,
}: ModalSheetProps) {
  const { connectRef: connectScrollRef, scrollYStart } = useScrollOffsets()
  const close = onRequestClose
  const contextProps: ModalSheetContextProps = useMemo(
    () => ({
      connectScrollRef,
      scrollYStart,
      close,
      panel,
    }),
    [connectScrollRef, scrollYStart, close, panel],
  )
  const { isFirstResponder, stackingStyle } = useModalStacking({
    isOpen: showing,
    close,
    disableCloseOnEsc,
  })
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const composedSheetRef = useComposedRefs(ref, sheetRef)
  useOnClickOutside(
    sheetRef,
    isFirstResponder && !disableCloseOnOutsideClick ? close : undefined,
  )

  const [transitionRef, rendering, hidden] = useCssTransition(showing)

  return (
    rendering && (
      <FullscreenOverlay preventScroll showing={!hidden} ref={transitionRef}>
        <ModalSheetContext value={contextProps}>
          <div
            className={cx(
              styles.container,
              panel && styles.panel,
              hidden && styles.hidden,
            )}
          >
            <m.div
              className={cx(
                styles.sheetContainer,
                large && styles.large,
                Boolean(leftSidePanelContent) &&
                  styles.leftSidePanelContentSheetContainer,
              )}
              // The stacking transform would override the hidden state's
              // transform, so it only applies while showing.
              style={!hidden ? stackingStyle : undefined}
            >
              {leftSidePanelContent ? (
                <div
                  ref={composedSheetRef}
                  className={styles.modalGroup}
                  {...dropzoneProps}
                >
                  {leftSidePanelContent}
                  <div
                    className={cx(
                      styles.sheet,
                      styles.leftSidePanelContentSheet,
                    )}
                    data-test={testId}
                  >
                    {children}
                  </div>
                </div>
              ) : (
                <div
                  ref={composedSheetRef}
                  className={styles.sheet}
                  data-test={testId}
                  {...dropzoneProps}
                >
                  {children}
                </div>
              )}
            </m.div>
          </div>
        </ModalSheetContext>
      </FullscreenOverlay>
    )
  )
}

interface LeftSidePanelProps {
  children?: ReactNode
}

/**
 * Left side panel for the modal sheet.
 */
export function LeftSidePanel({ children }: LeftSidePanelProps) {
  return <div className={styles.leftSidePanel}>{children}</div>
}

interface LeftSidePanelHeadingProps {
  children?: ReactNode
}

/**
 * Heading for the left side panel.
 */
export function LeftSidePanelHeading({ children }: LeftSidePanelHeadingProps) {
  return <div className={styles.leftSidePanelHeading}>{children}</div>
}

interface LeftSidePanelTitleProps {
  children?: ReactNode
}

/**
 * Title for the left side panel.
 */
export function LeftSidePanelTitle({ children }: LeftSidePanelTitleProps) {
  return <div className={styles.leftSidePanelTitle}>{children}</div>
}

interface LeftSidePanelDescriptionProps {
  children?: ReactNode
}

/**
 * Description for the left side panel.
 */
export function LeftSidePanelDescription({
  children,
}: LeftSidePanelDescriptionProps) {
  return <div className={styles.leftSidePanelDescription}>{children}</div>
}

interface ModalSheetHeaderShellProps {
  children?: ReactNode
  subheader?: ReactNode
  size: 'sm' | 'base' | 'auto'
  bordered?: boolean
  /**
   * If `true`, will add border radius to the bottom as well since there is
   * no sibling to add it.
   */
  lonelyChild?: boolean
}

/**
 * The outer-most part of a Modal Sheet header.
 * For convenience, use `ModalSheetHeader`.
 */
export function ModalSheetHeaderShell({
  children,
  subheader,
  lonelyChild,
  bordered,
  size,
}: ModalSheetHeaderShellProps) {
  const { scrollYStart } = useContext(ModalSheetContext)
  const shadowOpacity = useTransform(scrollYStart, (v) => clamp(v / 50))
  return (
    <div
      className={cx(
        styles.headerShell,
        lonelyChild && styles.lonely,
        bordered && styles.bordered,
        !subheader && styles[`size-${size}`],
      )}
    >
      <div className={styles.headerContainer}>{children}</div>
      {subheader}
      <ScrollShadow opacity={shadowOpacity} />
    </div>
  )
}

export interface ModalSheetHeaderProps {
  children?: ReactNode
  subheader?: ReactNode
  withCloseButton?: boolean
  bordered?: boolean
  /**
   * If `true`, will add border radius to the bottom as well since there is
   * no sibling to add it.
   */
  lonelyChild?: boolean
  shellSize?: 'sm' | 'base' | 'auto'
  closeButtonTestId?: string
}

/**
 * Header for the modal sheet.
 */
export function ModalSheetHeader({
  children,
  subheader,
  withCloseButton,
  lonelyChild,
  bordered,
  closeButtonTestId,
  shellSize = 'base',
}: ModalSheetHeaderProps) {
  const { close, panel } = useContext(ModalSheetContext)
  const closeButton = withCloseButton && close && (
    <div className={cx(styles.closeBtnContainer)}>
      <ModalSheetHeaderCloseButton testId={closeButtonTestId} />
    </div>
  )
  return (
    <ModalSheetHeaderShell
      subheader={subheader}
      size={shellSize}
      bordered={bordered}
      lonelyChild={lonelyChild}
    >
      {panel && closeButton}
      <div className={cx(styles.headerContent)}>{children}</div>
      {!panel && closeButton}
    </ModalSheetHeaderShell>
  )
}

interface ModalSheetHeaderCloseButtonProps {
  testId?: string
}

/**
 * Closes the modal sheet it's rendered in.
 */
export function ModalSheetHeaderCloseButton({
  testId,
}: ModalSheetHeaderCloseButtonProps) {
  const { close, panel } = useContext(ModalSheetContext)

  return (
    <IconButton
      name={panel ? 'back' : 'close'}
      label="Close"
      className={styles.closeBtn}
      isDisabled={!close}
      onPress={close}
      testId={testId}
    />
  )
}

interface ModalSheetTitleProps {
  children: ReactNode
  emphasized?: boolean
  large?: boolean
  testId?: string
}

/**
 * Modal sheet title. Put this in a `ModalSheetHeader`.
 */
export function ModalSheetTitle({
  children,
  emphasized,
  large,
  testId,
}: ModalSheetTitleProps) {
  return (
    <div
      className={cx(
        styles.title,
        emphasized && styles.emphasized,
        large && styles.large,
      )}
      data-test={testId}
    >
      {children}
    </div>
  )
}

interface ModalSheetHeaderDescriptionProps {
  children: ReactNode
}

/**
 * Modal sheet header description. Put this in a `ModalSheetHeader`.
 */
export function ModalSheetHeaderDescription({
  children,
}: ModalSheetHeaderDescriptionProps) {
  return <div className={styles.headerDescription}>{children}</div>
}

interface ModalSheetTitleEmphasisProps {
  children?: ReactNode
}

/**
 * Emphasizes a span of text inside a `ModalSheetTitle`.
 */
export function ModalSheetTitleEmphasis({
  children,
}: ModalSheetTitleEmphasisProps) {
  return <span className={styles.emphasized}>{children}</span>
}

interface ModalSheetContentProps {
  children: ReactNode
  leftAligned?: boolean
  expandable?: boolean
  ref?: Ref<HTMLDivElement>
}

/**
 * Content for the Modal sheet. Will scroll. Sticks to the bottom on smaller screens.
 */
export function ModalSheetContent({
  children,
  leftAligned,
  expandable,
  ref,
}: ModalSheetContentProps) {
  const { connectScrollRef } = useContext(ModalSheetContext)
  const composedRef = useComposedRefs(ref, connectScrollRef)

  return (
    <div
      ref={composedRef}
      className={cx(
        styles.content,
        leftAligned && styles.leftAligned,
        expandable && styles.expandable,
      )}
    >
      {children}
    </div>
  )
}

interface ModalSheetSectionProps {
  children?: ReactNode
  spaced?: boolean
  horizontallySpaced?: boolean
  bordered?: boolean
}

/**
 * Bordered section, to be used in `ModalSheetContent`.
 */
export function ModalSheetSection({
  children,
  spaced,
  horizontallySpaced,
  bordered,
}: ModalSheetSectionProps) {
  return (
    <div
      className={cx(
        styles.section,
        spaced && styles.spaced,
        horizontallySpaced && styles.horizontallySpaced,
        bordered && styles.bordered,
      )}
    >
      {children}
    </div>
  )
}

interface ModalSheetFooterProps {
  children?: ReactNode
}

/**
 * Modal sheet footer.
 */
export function ModalSheetFooter({ children }: ModalSheetFooterProps) {
  return <div className={styles.footer}>{children}</div>
}

interface ModalSheetFooterButtonGroupProps {
  children?: ReactNode
  stacked?: boolean
}

/**
 * Button group for Modal sheet footer. Ensures
 * spacing between buttons. Use `Button` components as children.
 */
export function ModalSheetFooterButtonGroup({
  children,
  stacked,
}: ModalSheetFooterButtonGroupProps) {
  return (
    <div className={cx(styles.footerBtnGroup, stacked && styles.stacked)}>
      {Children.map(children, (c, i) => (
        <div
          key={i}
          className={cx(styles.footerBtn, stacked && styles.stacked)}
        >
          {c}
        </div>
      ))}
    </div>
  )
}

interface ModalSheetFooterSpacerProps {
  children?: ReactNode
}

/**
 * Footer items are right-aligned; put this between them to push the items
 * before it to the left.
 */
export function ModalSheetFooterSpacer({
  children,
}: ModalSheetFooterSpacerProps) {
  return <div className={styles.footerSpacer}>{children}</div>
}

interface ModalSheetFooterSectionProps {
  children?: ReactNode
}

/**
 * Footer section. Use this for non-button groups.
 */
export function ModalSheetFooterSection({
  children,
}: ModalSheetFooterSectionProps) {
  return <div className={styles.footerSection}>{children}</div>
}

interface ModalSheetContentBlockProps {
  children?: ReactNode
  bordered?: boolean
  expanded?: boolean
  testId?: string
}

/**
 * A padded block of content, optionally separated from the one above by a
 * border.
 */
export function ModalSheetContentBlock({
  children,
  testId,
  bordered,
  expanded,
}: ModalSheetContentBlockProps) {
  return (
    <div
      className={cx(
        styles.contentBlock,
        bordered && styles.bordered,
        expanded && styles.expanded,
      )}
      data-test={testId}
    >
      {children}
    </div>
  )
}

interface ModalSheetContentIllustrationProps {
  src?: string
  /**
   * Shown instead of `src` when set.
   */
  illustration?: FittedIllustration
  alt?: string
  title?: string
}

/**
 * Image used for modal content, for example in a Confirm.
 */
export function ModalSheetContentIllustration({
  src,
  illustration,
  alt,
  title,
}: ModalSheetContentIllustrationProps) {
  const Illustration = illustration
  return (
    <div className={styles.contentIllustration}>
      {Illustration ? (
        <Illustration className={styles.contentIllustrationImage} label={alt} />
      ) : (
        <img
          className={styles.contentIllustrationImage}
          src={src}
          alt={alt}
          title={title}
        />
      )}
    </div>
  )
}

type Align = 'left' | 'center' | 'right'

interface ModalSheetContentTitleProps {
  children?: ReactNode
  compact?: boolean
  align?: Align
  testId?: string
}

/**
 * Title within the modal sheet's content.
 */
export function ModalSheetContentTitle({
  children,
  testId,
  compact,
  align,
}: ModalSheetContentTitleProps) {
  return (
    <div
      className={cx(
        styles.contentTitle,
        align && styles[align],
        compact && styles.compact,
      )}
      data-test={testId}
    >
      {children}
    </div>
  )
}

interface ModalSheetContentDescriptionProps {
  children?: ReactNode
  compact?: boolean
  align?: Align
  testId?: string
}

/**
 * Description within the modal sheet's content.
 */
export function ModalSheetContentDescription({
  children,
  testId,
  compact,
  align,
}: ModalSheetContentDescriptionProps) {
  return (
    <div
      className={cx(
        styles.contentDescription,
        align && styles[align],
        compact && styles.compact,
      )}
      data-test={testId}
    >
      {children}
    </div>
  )
}

interface ModalSheetListItemProps {
  children: ReactNode
}

/**
 * Wrapper for a list item. Includes padding and border.
 */
export function ModalSheetListItem({ children }: ModalSheetListItemProps) {
  return <div className={styles.listItem}>{children}</div>
}

interface ModalSheetSpacerProps {
  children?: ReactNode
}

/**
 * Vertical space between blocks of content.
 */
export function ModalSheetSpacer({ children }: ModalSheetSpacerProps) {
  return <div className={styles.spacer}>{children}</div>
}

interface ModalSheetContentBoxProps {
  children: ReactNode
  color?: 'blue' | 'green' | 'red' | 'grey'
  testId?: string
}

/**
 * Padded box for content with optional colors.
 */
export function ModalSheetContentBox({
  children,
  color,
  testId,
}: ModalSheetContentBoxProps) {
  return (
    <div
      data-test={testId}
      className={cx(styles.contentBox, color && styles[color])}
    >
      {children}
    </div>
  )
}
